/**
 * Glass-Over-World snapshot backdrop helper (Phaser-wired shell).
 *
 *  +----------------------------------------------------------------+
 *  |  WorldScene (source) ──── captureFrom() ──── SnapshotHandle    |
 *  |       │                                          │             |
 *  |       │  game.renderer.snapshot(cb)   OR         │             |
 *  |       │  100ms timeout → null                    │             |
 *  |       ▼                                          ▼             |
 *  |  textures.addImage(uniqueKey, frameImage)                      |
 *  |       │                                                        |
 *  |       ▼                                                        |
 *  |  sourceScene.add.image(0, 0, key)  ◄── handle.image            |
 *  |                                                                |
 *  |  ── later, in modal scene create() ──                          |
 *  |  handle.transferOwnership(modalScene)                          |
 *  |       │  destroys old Image                                    |
 *  |       │  modalScene.add.image(0, 0, key)  ◄── new handle.image |
 *  |       │  texture survives in global TextureManager             |
 *  |       ▼                                                        |
 *  |  modal scene SHUTDOWN ──── handle.dispose()                    |
 *  |       │  destroys Image                                        |
 *  |       │  game.textures.remove(key)                             |
 *  +----------------------------------------------------------------+
 *
 * Capture mechanism (rewritten 2026-08-01): `game.renderer.snapshot()`
 * grabs the real composited framebuffer at the end of the current
 * render pass — exactly what the player saw, camera zoom included.
 * The original implementation allocated a DynamicTexture and mirrored
 * the world camera's scroll/zoom onto dt.camera before dt.draw(); the
 * DT mini-camera does NOT reproduce a zoomed main camera's framing,
 * so every capture rendered the world as a small mis-placed thumbnail
 * (silent — the battle backdrop just looked like a void). The
 * framebuffer path can't drift from the on-screen truth by
 * construction.
 *
 * Locked decisions honored:
 *   #1  The texture lives in the GLOBAL TextureManager
 *       (textures.addImage), never a scene-owned RenderTexture — it
 *       must survive the host scene's SHUTDOWN for the BattleScene →
 *       EndRunScene handoff.
 *   #2  100ms timeout race; on timeout, null → caller falls back to
 *       the prior `#0E1014` opaque backdrop. (renderer.snapshot
 *       resolves at the end of the in-flight frame — if the game loop
 *       is halted, the timeout fires instead.)
 *   #4  Caller owns the handle until create() transfers ownership.
 *   #7  flashOverlay pre-filter: a display list still holding the
 *       encounter flash refuses to capture (the framebuffer would be
 *       white-saturated).
 *   #10 The texture survives transferOwnership; only the Image moves.
 */

import type { Scene, GameObjects } from 'phaser';
import {
  generateSnapshotKey,
  resolveCaptureState,
  validateCaptureInputs,
  type CaptureState,
} from './snapshotBackdropLogic';
import { computeOwnershipTransfer } from './snapshotBackdropLogic';

// String value of Phaser.Scenes.Events.SHUTDOWN as of 3.90. Inlined so
// this module needs no Phaser runtime import (vitest drives captureFrom
// with duck-typed mock scenes).
const SCENE_EVENT_SHUTDOWN = 'shutdown';

/** Duck-typed slice of Phaser.Renderer.(WebGL|Canvas) — both expose
 *  snapshot(cb) resolving with an HTMLImageElement of the frame. */
interface RendererLike {
  snapshot(cb: (image: unknown) => void): unknown;
}

export interface SnapshotHandle {
  readonly textureKey: string;
  /** Current scene-parented Image wrapping the global texture. */
  readonly image: GameObjects.Image;
  /** Tear down: destroy Image, remove texture from global manager. */
  dispose(): void;
  /** Re-capture the current framebuffer into the same key (resize /
   *  orientation). No-ops with a one-time warn if the host scene is
   *  no longer the one on screen. */
  invalidate(): void;
  /** Destroy old Image, create a new one in toScene; texture survives. */
  transferOwnership(toScene: Scene): SnapshotHandle;
}

export interface CaptureOptions {
  /** Display-list depth for the snapshot Image. Defaults to 0 (back). */
  depth?: number;
  /** Resolve to null if the renderer snapshot doesn't land within this
   *  many ms. Defaults to 100. */
  timeoutMs?: number;
}

/**
 * Capture `sourceScene`'s game canvas (the composited framebuffer —
 * whatever is on screen this frame) into a global texture, wrapped in
 * a scene-owned Image parented to `sourceScene` initially. The caller
 * invokes `handle.transferOwnership(modalScene)` once the modal
 * scene's create() runs (eng-locked #4).
 *
 * Resolves to `null` on timeout or any error during capture; the
 * caller falls back to the prior `#0E1014` opaque backdrop. Logs a
 * single WARN on timeout.
 */
export async function captureFrom(
  sourceScene: Scene,
  opts?: CaptureOptions,
): Promise<SnapshotHandle | null> {
  const width = sourceScene.scale?.gameSize?.width ?? 0;
  const height = sourceScene.scale?.gameSize?.height ?? 0;
  const textureKey = generateSnapshotKey();

  let request: ReturnType<typeof validateCaptureInputs>;
  try {
    request = validateCaptureInputs(sourceScene as unknown as Parameters<typeof validateCaptureInputs>[0], {
      width,
      height,
      timeoutMs: opts?.timeoutMs,
      depth: opts?.depth,
      textureKey,
    });
  } catch (err) {
    console.warn('[snapshotBackdrop] validation failed', err);
    return null;
  }

  let state: CaptureState = 'idle';
  state = resolveCaptureState(state, 'START');

  // Race the renderer's end-of-frame capture against the timeout.
  // Whichever settles first wins; the loser lands as a no-op.
  const winner = await new Promise<{ kind: 'frame'; image: unknown } | { kind: 'timeout' }>(
    (resolve) => {
      let settled = false;
      const timeoutHandle = setTimeout(() => {
        if (settled) return;
        settled = true;
        resolve({ kind: 'timeout' });
      }, request.timeoutMs);
      const renderer = (
        sourceScene.game as unknown as { renderer?: RendererLike }
      ).renderer;
      if (!renderer || typeof renderer.snapshot !== 'function') {
        // Headless / mock game without a renderer — let the timeout
        // resolve the race so the caller falls back cleanly.
        return;
      }
      try {
        renderer.snapshot((image) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutHandle);
          resolve({ kind: 'frame', image });
        });
      } catch (err) {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutHandle);
        console.warn('[snapshotBackdrop] renderer.snapshot threw', err);
        resolve({ kind: 'timeout' });
      }
    },
  );

  if (winner.kind === 'timeout') {
    state = resolveCaptureState(state, 'TIMEOUT');
    console.warn(
      `[snapshotBackdrop] renderer snapshot did not land within ${request.timeoutMs}ms; falling back`,
    );
    return null;
  }

  // Frame captured. Register it as a global static texture.
  try {
    const tex = (
      sourceScene.textures as unknown as {
        addImage(key: string, img: unknown): unknown | null;
      }
    ).addImage(request.textureKey, winner.image);
    if (!tex) {
      throw new Error(`addImage returned null for key ${request.textureKey}`);
    }
  } catch (err) {
    state = resolveCaptureState(state, 'ERROR');
    console.warn('[snapshotBackdrop] capture failed', err);
    if (sourceScene.textures.exists(request.textureKey)) {
      sourceScene.textures.remove(request.textureKey);
    }
    return null;
  }

  state = resolveCaptureState(state, 'COMPLETE');
  void state; // terminal — referenced for the state-machine contract.
  return buildHandle(sourceScene, request.textureKey, request.depth, request.width, request.height);
}

function buildHandle(
  hostScene: Scene,
  textureKey: string,
  depth: number,
  width: number,
  height: number,
): SnapshotHandle {
  let disposed = false;
  let invalidateWarnedOnce = false;
  let currentScene: Scene = hostScene;
  // setDisplaySize pins the on-screen size to the logical canvas size
  // even if the captured framebuffer is DPR-scaled.
  let currentImage: GameObjects.Image = hostScene.add
    .image(0, 0, textureKey)
    .setOrigin(0)
    .setDepth(depth)
    .setDisplaySize(width, height);

  // Defensive: if the host scene shuts down before transferOwnership
  // is called (e.g. modal init throws between captureFrom and modal
  // create()), tear everything down so the texture doesn't leak. The
  // listener moves with the handle on transferOwnership.
  const shutdownListener = (): void => {
    handleRef.dispose();
  };
  currentScene.events.once(SCENE_EVENT_SHUTDOWN, shutdownListener);

  const handleRef: SnapshotHandle = {
    textureKey,
    get image(): GameObjects.Image {
      return currentImage;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      currentScene.events.off(SCENE_EVENT_SHUTDOWN, shutdownListener);
      currentImage.destroy();
      const game = currentScene.game;
      if (game?.textures?.exists(textureKey)) {
        game.textures.remove(textureKey);
      }
    },
    invalidate(): void {
      if (disposed) return;
      const src = currentScene;
      if (!src.scene.isActive() && !src.scene.isVisible()) {
        if (!invalidateWarnedOnce) {
          console.warn(
            '[snapshotBackdrop] invalidate() while host inactive; texture stays stale',
          );
          invalidateWarnedOnce = true;
        }
        return;
      }
      const renderer = (src.game as unknown as { renderer?: RendererLike }).renderer;
      if (!renderer || typeof renderer.snapshot !== 'function') return;
      renderer.snapshot((image) => {
        if (disposed) return;
        // Phaser invokes the callback with NO argument on Image
        // decode error (WebGLSnapshot.js image.onerror) — bail, keep
        // the stale-but-valid texture rather than destroying it.
        if (!image) {
          console.warn('[snapshotBackdrop] invalidate(): renderer delivered no frame; texture stays stale');
          return;
        }
        const tm = src.game.textures as unknown as {
          exists(key: string): boolean;
          remove(key: string): void;
          addImage(key: string, img: unknown): unknown | null;
        };
        // Add under a temp key FIRST so a failed add can never leave
        // the live Image bound to a destroyed texture; only swap +
        // remove the old key once the fresh capture is registered.
        const tempKey = `${textureKey}:next`;
        if (tm.exists(tempKey)) tm.remove(tempKey);
        let added: unknown | null = null;
        try {
          added = tm.addImage(tempKey, image);
        } catch (err) {
          console.warn('[snapshotBackdrop] invalidate(): addImage failed; texture stays stale', err);
          return;
        }
        if (!added) return;
        currentImage.setTexture(tempKey);
        if (tm.exists(textureKey)) tm.remove(textureKey);
        tm.addImage(textureKey, image);
        currentImage.setTexture(textureKey).setDisplaySize(width, height);
        tm.remove(tempKey);
      });
    },
    transferOwnership(toScene: Scene): SnapshotHandle {
      if (disposed) {
        throw new Error('[snapshotBackdrop] transferOwnership on disposed handle');
      }
      const plan = computeOwnershipTransfer({
        textureKey,
        currentScene,
        targetScene: toScene,
      });
      if (plan.noop) return handleRef;

      currentScene.events.off(SCENE_EVENT_SHUTDOWN, shutdownListener);
      currentImage.destroy();
      currentImage = toScene.add
        .image(0, 0, textureKey)
        .setOrigin(0)
        .setDepth(depth)
        .setDisplaySize(width, height);
      currentScene = toScene;
      toScene.events.once(SCENE_EVENT_SHUTDOWN, shutdownListener);
      return handleRef;
    },
  };

  return handleRef;
}
